import { Injectable } from '@angular/core';
import { CreativeAuthApiService } from '../../api/auth/creative-auth-api.service';
import { SnackbarActionEnum, SnackBarService } from '../../utils/snack-bar.service';
import { handleApiCallAsync } from '../../../utils/general-functions';
import { WorkspaceAssetApiManagerService } from '../../workspace/api/managers/associations/workspace-asset-api-manager.service';
import { IGenerateImageRequest, IGenerateVideoRequest } from '../../http-models/asset/asset-http-request-model';
import { IllegalArgumentException } from '../../../models/errors/general.errors';
import {
    IGenerateAssetFunctionParams,
    IGeneratedAssetResponse,
} from '../../../pages/private/dashboard/assets/assets-main/assets-main-manager.service';

export interface IExternalLibraryUpload {
    from: 'unsplash' | 'pexels' | 'shutterstock' | 'pixabay';
    imageId: string;
}

@Injectable()
export class AssetUtilsService {
    constructor(
        private creativeApiService: CreativeAuthApiService,
        private snackBarService: SnackBarService,
        private workspaceAssetManager: WorkspaceAssetApiManagerService
    ) {}

    async generateAssetLogic(
        generateAssetParams: IGenerateAssetFunctionParams,
        folderId: string
    ): Promise<IGeneratedAssetResponse> {
        const { assetId, model, aspectRatio, prompt, toUseLogo, projectId } = generateAssetParams;
        if (!prompt) {
            this.snackBarService.openMessage(`Please describe your image first`, SnackbarActionEnum.AWESOME, 5000);
            return;
        }

        const body: IGenerateImageRequest = assetId
            ? {
                  projectId,
                  toStartFromAsset: { assetId, enrichImageWithText: prompt },
                  startFromSimpleText: null,
                  folderId: folderId ?? null,
                  model,
                  aspectRatio,
                  toUseLogo,
              }
            : {
                  projectId,
                  toStartFromAsset: null,
                  startFromSimpleText: { text: prompt },
                  folderId: folderId ?? null,
                  model,
                  aspectRatio,
                  toUseLogo,
              };

        const { data, error } = await this.workspaceAssetManager.generateImageAsync(body);

        if (error || !data) {
            this.snackBarService.openMessage(
                `An error occurred while generating image`,
                SnackbarActionEnum.Close,
                5000
            );
            return;
        }

        return { uploadPath: data.uploadPath, assetId: data.assetId, asset: data.asset };
    }

    async uploadPhotoFromPhotoLibraryAsync(body: IExternalLibraryUpload, toFolder: string) {
        return await this.workspaceAssetManager.uploadPhotoFromPhotoLibraryAsync(body, toFolder);
    }

    async generateVideoAsync(body: IGenerateVideoRequest) {
        if (!body || (!body.toStartFromAsset && !body.startFromSimpleText && !body.extendVideo)) {
            throw new IllegalArgumentException(`No body to generate video`);
        }
        const { toStartFromAsset, startFromSimpleText } = body;
        if (toStartFromAsset && startFromSimpleText) {
            throw new IllegalArgumentException(`Cannot start from text and asset to generate video`);
        }
        if (startFromSimpleText) {
            if (!startFromSimpleText.text) {
                throw new IllegalArgumentException(`No text to start from simple text to generate video`);
            }
        }
        if (toStartFromAsset) {
            if (!toStartFromAsset.assetId) {
                throw new IllegalArgumentException(`No text to start from simple text to generate video`);
            }
        }
        const { data, error } = await this.workspaceAssetManager.generateVideoAsync(body);
        if (error || !data) {
            this.snackBarService.openMessage(
                `An error occurred while generating Video`,
                SnackbarActionEnum.Close,
                5000
            );
            return;
        }

        return data;
    }
}
